/**
 * Шаг «Колонки файла» импорта структуры из своего файла (structure-import-custom-file, design D2.3/D2.7).
 * Сервер уже распознал шапку и поля колонок (`ImportAnalysis`); пользователь проверяет и правит
 * разметку — неверно понятые колонки молча испортили бы структуру, поэтому шаг обязателен.
 *
 * - Лист выбирается, если в файле несколько листов (таблиц): смена листа запрашивает анализ заново.
 * - Каждой колонке — поле или «Не загружать»; одно поле — одна колонка.
 * - Нашлись строки-заголовки (подразделение или должность строкой над группой сотрудников,
 *   structure-import-formats) — пользователь подтверждает их роль: кабинет предлагает её по
 *   распознанным колонкам, но «АУП» от «Электрогазосварщик 6 разряда» по форме файла не отличить.
 * - Подразделение неоткуда взять (нет ни колонки, ни заголовков в роли подразделения) — обязательное
 *   «Подразделение для всех строк» (список подразделений клиента или новое наименование).
 * - «Продолжить» доступно, когда размечены должность, ФИО (одной колонкой или фамилия + имя) и
 *   подразделение — та же проверка, что на сервере. Файл уходит в предпросмотр вместе с разметкой.
 */
import { useEffect, useState } from 'react'
import {
  ApiError,
  type CompanyScopeApi,
  type Department,
  type ImportAnalysis,
  type ImportColumnField,
  type ImportGroupRole,
  type ImportPreview,
  type ImportPreviewOptions,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  assignStructureImportField,
  initialStructureImportMapping,
  structureImportMappingIssue,
  structureImportNeedsDefaultDepartment,
  type StructureImportMapping,
} from '@/lib/structure'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { Field } from '@/design-system/components/primitives/field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { DepartmentSelect } from './department-select'

/** Значение «Не загружать»: Radix Select не принимает пустую строку. */
const SKIP_FIELD = 'skip'

/** Подписи ролей строк-заголовков. */
const GROUP_ROLE_LABELS: Record<ImportGroupRole, string> = {
  department: 'Подразделение',
  position: 'Должность',
  skip: 'Не загружать',
}

export interface StructureImportMappingProps {
  api: CompanyScopeApi
  file: File
  analysis: ImportAnalysis
  /** Предпросмотр по подтверждённой разметке получен. */
  onPreview: (preview: ImportPreview) => void
  /** Вернуться к выбору файла. */
  onBack: () => void
}

/** «строка» / «строки» / «строк» — счётная форма для подсказки о заголовках. */
function groupRowsWord(count: number): string {
  const tens = count % 100
  const ones = count % 10
  if (tens >= 11 && tens <= 14) return 'строк'
  if (ones === 1) return 'строка'
  if (ones >= 2 && ones <= 4) return 'строки'
  return 'строк'
}

export function StructureImportMapping({
  api,
  file,
  analysis: initialAnalysis,
  onPreview,
  onBack,
}: StructureImportMappingProps) {
  const [analysis, setAnalysis] = useState(initialAnalysis)
  const [mapping, setMapping] = useState<StructureImportMapping>(() =>
    initialStructureImportMapping(initialAnalysis.columns),
  )
  const [defaultDepartment, setDefaultDepartment] = useState('')
  // Роль строк-заголовков: стартует с предложенной сервером; при смене листа берётся заново.
  const [groupRole, setGroupRole] = useState<ImportGroupRole | null>(
    initialAnalysis.groups?.role ?? null,
  )
  const [departments, setDepartments] = useState<Department[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    api
      .listDepartments(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setDepartments(res.items)
      })
      // Список — только подсказка: без него подразделение вводится текстом.
      .catch(() => undefined)
    return () => controller.abort()
  }, [api])

  const groups = analysis.groups
  const needsDefaultDepartment = structureImportNeedsDefaultDepartment(mapping, groupRole)
  const issue = structureImportMappingIssue(
    mapping,
    needsDefaultDepartment ? defaultDepartment : '',
    groupRole,
  )

  function errorText(err: unknown, fallback: string): string {
    if (err instanceof ApiError && err.details && err.details.length > 0) {
      return err.details[0].message
    }
    return getErrorMessage(err, fallback)
  }

  async function changeSheet(sheet: string) {
    if (sheet === analysis.sheet) return
    setBusy(true)
    setError(null)
    try {
      const next = await api.analyzeImport(file, sheet)
      setAnalysis(next)
      setMapping(initialStructureImportMapping(next.columns))
      setGroupRole(next.groups?.role ?? null)
    } catch (err) {
      setError(errorText(err, 'Не удалось прочитать лист'))
    } finally {
      setBusy(false)
    }
  }

  async function submit() {
    if (issue) return
    setBusy(true)
    setError(null)
    try {
      const options: ImportPreviewOptions = {
        sheet: analysis.sheet,
        headerRow: analysis.headerRow,
        mapping,
        defaultDepartment: needsDefaultDepartment ? defaultDepartment.trim() : undefined,
        groupRole: groupRole ?? undefined,
      }
      onPreview(await api.uploadImportPreview(file, options))
    } catch (err) {
      setError(errorText(err, 'Не удалось разобрать файл'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      header="Колонки файла"
      footer={
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void submit()} disabled={busy || issue !== null}>
            {busy ? 'Загрузка…' : 'Продолжить'}
          </Button>
          <Button variant="secondary" onClick={onBack} disabled={busy}>
            Назад
          </Button>
        </div>
      }
    >
      <p className="mb-4 text-14 text-txt-secondary">
        Проверьте, как кабинет понял колонки файла «{file.name}». Колонки с отметкой «Не загружать»
        в структуру не попадут. Данные начинаются со строки {analysis.headerRow + 1}.
      </p>
      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {analysis.sheets.length > 1 ? (
        <Field label="Лист" className="mb-4 mt-0 max-w-sm">
          {(props) => (
            <Select
              value={analysis.sheet}
              onValueChange={(v) => void changeSheet(v)}
              disabled={busy}
            >
              <SelectTrigger id={props.id} aria-label="Лист">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {analysis.sheets.map((sh) => (
                  <SelectItem key={sh.name} value={sh.name}>
                    {sh.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      ) : null}

      <div className="mb-4 overflow-x-auto">
        <DataTable>
          <DataTableHead>
            <DataTableRow>
              <DataTableHeaderCell>Колонка файла</DataTableHeaderCell>
              <DataTableHeaderCell>Примеры значений</DataTableHeaderCell>
              <DataTableHeaderCell>Загрузить как</DataTableHeaderCell>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {analysis.columns.map((column) => {
              const title = column.header || `Колонка ${column.index + 1}`
              return (
                <DataTableRow key={column.index}>
                  <DataTableCell className="font-medium text-txt">{title}</DataTableCell>
                  <DataTableCell className="text-txt-secondary">
                    {column.samples.length > 0 ? column.samples.join(' · ') : '—'}
                  </DataTableCell>
                  <DataTableCell>
                    <Select
                      value={mapping[column.index] ?? SKIP_FIELD}
                      disabled={busy}
                      onValueChange={(v) =>
                        setMapping((prev) =>
                          assignStructureImportField(
                            prev,
                            column.index,
                            v === SKIP_FIELD ? '' : (v as ImportColumnField),
                          ),
                        )
                      }
                    >
                      <SelectTrigger
                        className="w-56"
                        aria-label={`Загрузить колонку «${title}» как`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SKIP_FIELD}>Не загружать</SelectItem>
                        {analysis.fields.map((f) => (
                          <SelectItem key={f.field} value={f.field}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DataTableCell>
                </DataTableRow>
              )
            })}
          </DataTableBody>
        </DataTable>
      </div>

      {groups && groupRole ? (
        <Field
          label="Строки-заголовки в файле"
          hint={`В файле ${groups.count} ${groupRowsWord(groups.count)}, где заполнена только одна ячейка: ${groups.samples.map((v) => `«${v}»`).join(', ')}. Укажите, что это, — значение применится ко всем строкам под заголовком.`}
          className="mb-4 mt-0 max-w-md"
        >
          {(props) => (
            <Select
              value={groupRole}
              disabled={busy}
              onValueChange={(v) => setGroupRole(v as ImportGroupRole)}
            >
              <SelectTrigger
                id={props.id}
                className="w-56"
                aria-label="Строки-заголовки в файле"
                aria-describedby={props['aria-describedby']}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['department', 'position', 'skip'] as const).map((role) => (
                  <SelectItem key={role} value={role}>
                    {GROUP_ROLE_LABELS[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      ) : null}

      {needsDefaultDepartment ? (
        <Field
          label="Подразделение для всех строк"
          required
          hint="В файле нет колонки подразделения. Строки-группы подразделений в файле заменят его для следующих строк; уже внесённые сотрудники при обновлении останутся в своих подразделениях."
          className="mb-4 mt-0 max-w-md"
        >
          {(props) => (
            <DepartmentSelect
              departments={departments}
              value={defaultDepartment}
              onSelect={(name) => setDefaultDepartment(name)}
              deferCreate
              id={props.id}
              aria-required={props['aria-required']}
              aria-describedby={props['aria-describedby']}
              placeholder="Выберите или введите подразделение"
              disabled={busy}
            />
          )}
        </Field>
      ) : null}

      {issue ? (
        <Alert variant="info" className="mb-0">
          {issue}
        </Alert>
      ) : null}
    </Card>
  )
}
